import { Button, Tab, Tabs, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Skeleton } from '@material-ui/lab';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';

import { usePostHog } from 'hooks/usePostHog';
import globalMessages from 'shared/global/messages';

import { AI_AGENT_SETUP } from '../constants';
import { EditKnowledgeBaseButton } from '../EditKnowledgeBaseButton';
import { messages } from '../messages';
import { ConversationFilterBar } from './ConversationFilterBar';
import {
  ConversationFilters,
  isNarrowed,
  parseConversationFilters,
  toRequestParams,
  writeConversationFilters,
} from './conversationFilters';
import {
  readConversationParams,
  writeConversationParams,
} from './conversationUrlState';
import { useStyles } from './styles';
import { TurnRow } from './TurnRow';
import { TranscriptPanel } from './TranscriptPanel';
import { transcriptUrlKeys } from './transcriptUrlKeys';
import { useConversationTurns } from './useConversationTurns';

export const ConversationsPage = ({ accountSlug }: { accountSlug: string }) => {
  const { agentId } = useParams<{ agentId: string }>();
  const history = useHistory();
  const location = useLocation();
  const pageRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const { thread, turn: turnId } = readConversationParams(
    location.search,
    transcriptUrlKeys,
  );
  const closeTranscript = useCallback(() => {
    const threadRows = Array.from(
      pageRef.current?.querySelectorAll<HTMLElement>(
        '[role="button"][data-turn-id]',
      ) ?? [],
    ).filter(element => element.dataset.threadId === thread);
    const row =
      threadRows.find(element => element.dataset.turnId === turnId) ??
      threadRows[0];
    history.replace({
      ...location,
      search: writeConversationParams(location.search, {
        thread: null,
        turn: null,
      }),
    });
    (row ?? headingRef.current)?.focus({ preventScroll: true });
  }, [history, location, thread, turnId]);
  const intl = useIntl();
  const classes = useStyles();
  const posthog = usePostHog();
  const viewed = useRef(false);
  const filters = parseConversationFilters(location.search);
  const filterKey = JSON.stringify(filters);
  const now = useRef(new Date());
  const requestFilters = useMemo(
    () =>
      toRequestParams(
        JSON.parse(filterKey) as ConversationFilters,
        now.current,
      ),
    [filterKey],
  );
  const changeFilters = (next: ConversationFilters) => {
    history.push({
      ...location,
      search: writeConversationFilters(next, location.search),
    });
  };
  const narrowed = isNarrowed(filters);
  const {
    status,
    agent,
    turns,
    nextCursor,
    loadingMore,
    loadMoreError,
    retry,
    loadMore,
  } = useConversationTurns(accountSlug, agentId, requestFilters);

  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    posthog.capture('candidate_ai_agent:conversations_view', {
      agent_id: agentId,
    });
  }, [posthog, agentId]);

  const errorBar = (onRetry: () => void) => (
    <div className={classes.error} role="alert">
      <ErrorOutlineIcon />
      <Typography variant="body2" className={classes.primary}>
        {intl.formatMessage(messages.loadError)}
      </Typography>
      <Button className={classes.retry} onClick={onRetry}>
        {intl.formatMessage(globalMessages.tryAgain)}
      </Button>
    </div>
  );

  return (
    <div className={classes.page} ref={pageRef}>
      <Button
        component={Link}
        to={`/${accountSlug}/fountain_ai/${AI_AGENT_SETUP}`}
        className={classes.back}
        startIcon={<ArrowBackIcon />}
      >
        {intl.formatMessage(messages.conversationsBack)}
      </Button>
      <div className={classes.header}>
        <Typography variant="h2" component="h2" ref={headingRef} tabIndex={-1}>
          {agent?.name ?? intl.formatMessage(messages.conversationsTab)}
        </Typography>
        <Typography variant="body2" className={classes.muted}>
          {agent?.brand}
        </Typography>
      </div>
      <Tabs
        value="conversations"
        className={classes.tabs}
        aria-label={intl.formatMessage(messages.conversationsTab)}
      >
        <Tab
          value="conversations"
          className={classes.tab}
          id="conversations-tab"
          aria-controls="conversations-content"
          label={intl.formatMessage(messages.conversationsTab)}
        />
      </Tabs>
      <ConversationFilterBar
        accountSlug={accountSlug}
        agentId={agentId}
        filters={filters}
        onChange={changeFilters}
      />
      <div
        id="conversations-content"
        role="tabpanel"
        aria-labelledby="conversations-tab"
      >
        {status === 'too_many_openings' && (
          <div className={classes.card}>
            <div className={classes.empty}>
              <Typography variant="h3">
                {intl.formatMessage(messages.pickOneOpening)}
              </Typography>
            </div>
          </div>
        )}
        {status === 'error'
          ? errorBar(retry)
          : status !== 'too_many_openings' && (
              <div
                className={classes.card}
                data-testid="conversations-list"
                aria-busy={status === 'loading' || loadingMore}
              >
                <div
                  className={classNames(classes.grid, classes.listHeader)}
                  aria-hidden="true"
                >
                  {[
                    messages.columnApplicant,
                    messages.columnQuestion,
                    messages.columnAnswer,
                    globalMessages.date,
                  ].map(message => (
                    <Typography variant="body2" key={message.id}>
                      {intl.formatMessage(message)}
                    </Typography>
                  ))}
                  <div />
                </div>
                {status === 'loading' &&
                  Array.from({ length: 6 }, (_, index) => (
                    <div
                      className={classNames(classes.grid, classes.loadingRow)}
                      key={index}
                      aria-hidden="true"
                    >
                      {[
                        [120, 88],
                        ['100%', '60%'],
                        ['100%', '100%', '40%'],
                        [96],
                        [],
                      ].map((widths, cell) => (
                        <div key={cell}>
                          {widths.map((width, line) => (
                            <Skeleton
                              key={line}
                              variant="rect"
                              className={classes.skeleton}
                              width={width}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                {status === 'ready' && !turns.length && (
                  <div className={classes.empty}>
                    <Typography variant="h3">
                      {intl.formatMessage(
                        narrowed
                          ? messages.emptyFiltered
                          : messages.emptyNoConversationsTitle,
                      )}
                    </Typography>
                    {narrowed ? (
                      <Button
                        variant="outlined"
                        className={classes.moreButton}
                        onClick={() => changeFilters({ range: '30d' })}
                      >
                        {intl.formatMessage(messages.clearFilters)}
                      </Button>
                    ) : (
                      <Typography variant="body2" className={classes.secondary}>
                        {intl.formatMessage(messages.emptyNoConversationsBody)}
                      </Typography>
                    )}
                  </div>
                )}
                {turns.map(turn => (
                  <TurnRow
                    key={turn.id}
                    turn={turn}
                    renderAction={() => (
                      <EditKnowledgeBaseButton
                        agentId={agentId}
                        surface="row"
                      />
                    )}
                    isOpen={turn.thread_id === thread && turn.id === turnId}
                    onOpen={selected =>
                      history.replace({
                        ...location,
                        search: writeConversationParams(location.search, {
                          thread: selected.thread_id,
                          turn: selected.id,
                        }),
                      })
                    }
                  />
                ))}
              </div>
            )}
        {status === 'ready' && turns.length > 0 && (
          <div className={classes.more}>
            {loadMoreError && errorBar(loadMore)}
            {nextCursor ? (
              <Button
                variant="outlined"
                className={classes.moreButton}
                disabled={loadingMore}
                onClick={loadMore}
              >
                {intl.formatMessage(globalMessages.loadMore)}
              </Button>
            ) : (
              <Typography variant="body2" className={classes.muted}>
                {intl.formatMessage(messages.endOfList)}
              </Typography>
            )}
          </div>
        )}
      </div>
      <div data-testid="conversations-panel">
        {thread && (
          <TranscriptPanel
            key={`${accountSlug}:${agentId}:${thread}:${turnId ?? ''}:${filters.opening ?? ''}`}
            accountSlug={accountSlug}
            agentId={agentId}
            threadUuid={thread}
            turnId={turnId}
            opening={filters.opening}
            onClose={closeTranscript}
            renderFooter={() => (
              <EditKnowledgeBaseButton agentId={agentId} surface="panel" />
            )}
          />
        )}
      </div>
    </div>
  );
};
